import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { CheckoutShell } from './CheckoutShell.jsx';
import { CheckoutNotFound } from './CheckoutNotFound.jsx';
import { http } from '../../lib/api.js';

/**
 * PL-A: "A reusable URL that mints a fresh payment and checkout session each time
 * it is opened, so one link can collect many payments."
 */
export function PaymentLinkPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  // StrictMode double-invokes effects in development; without this guard the
  // link's use count would be consumed twice on every local page load.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    http
      .post(`/checkout/links/${code}`)
      .then((payment) => navigate(`/c/${payment.checkoutSessionId}`, { replace: true }))
      .catch(setError);
  }, [code, navigate]);

  if (error) {
    const messages = {
      link_inactive: 'This payment link is no longer active. Ask the merchant for a new one.',
      link_expired: 'This payment link has expired. Ask the merchant for a new one.',
      link_exhausted: 'This payment link has already been used the maximum number of times.',
      no_provider_connected: 'This store cannot take payments right now. Please contact the merchant.',
    };
    return <CheckoutNotFound message={messages[error.code] ?? error.message} />;
  }

  return (
    <CheckoutShell merchant={null} payment={null} showTrust={false}>
      <div className="grid place-items-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-faint" role="status" aria-label="Preparing your checkout" />
        <p className="mt-3 text-sm text-muted">Preparing your checkout…</p>
      </div>
    </CheckoutShell>
  );
}

export default PaymentLinkPage;
